/**
 * InputFecha — input único de fecha para todo el portal (P27).
 *
 * Encapsula la doctrina de fechas del proyecto en un solo lugar para que
 * NINGÚN formulario dependa del locale del navegador:
 *  - El usuario teclea **dd-mm-aaaa** (máscara en vivo, locale-independiente).
 *  - Internamente valida que sea una fecha calendario real.
 *  - Emite al padre el ISO **yyyy-mm-dd** (o '' si está incompleta/inválida
 *    o fuera de rango), igual que hacía el `<input type="date">` nativo.
 *  - Muestra error inline tras tocar el campo.
 *
 * Reemplaza a `<input type="date">`, cuyo picker se renderiza según el
 * locale del navegador: en un browser en-US, tipear "01.06.2026" (1-jun)
 * se interpretaba mes-primero → 2026-01-06 (6-ene). Bug real del ensayo
 * LIVE 2026-06. La doctrina es dd-mm-aaaa, NO estilo USA.
 *
 * Es controlado igual que el nativo: `value` es el ISO que guarda el
 * padre; el componente muestra dd-mm-aaaa derivado de ese ISO y, al
 * teclear, emite el ISO de vuelta. `min`/`max` son ISO (como el nativo).
 */
import { useEffect, useRef, useState } from 'react';
import {
  displayFechaAIso,
  formatearFechaEnVivo,
  isoAFechaDisplay,
} from '../../lib/tiempo';

interface InputFechaProps {
  /** Valor ISO `yyyy-mm-dd` (o ''). Misma semántica que el input nativo. */
  value: string;
  /** Recibe el ISO `yyyy-mm-dd` válido dentro de rango, o '' si no. */
  onChange: (iso: string) => void;
  id?: string;
  /** Mínimo permitido, ISO `yyyy-mm-dd`. */
  min?: string;
  /** Máximo permitido, ISO `yyyy-mm-dd`. */
  max?: string;
  /** Si true, un valor vacío muestra error tras tocar. Default false. */
  requerido?: boolean;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

const CLASE_BASE =
  'w-full rounded-sm bg-bg-sunken border-[0.5px] border-white/15 ' +
  'text-text-primary px-3 py-2 text-sm';

export function InputFecha({
  value,
  onChange,
  id,
  min,
  max,
  requerido = false,
  disabled = false,
  className = '',
  'aria-label': ariaLabel,
}: InputFechaProps) {
  const [display, setDisplay] = useState(() => isoAFechaDisplay(value));
  const [tocado, setTocado] = useState(false);
  // Último ISO que ESTE componente emitió. Sirve para distinguir un cambio
  // externo de `value` (precarga, reset del padre) de nuestro propio emit,
  // y resincronizar el display solo cuando el cambio vino de afuera.
  const ultimoEmitido = useRef(value);

  useEffect(() => {
    if (value !== ultimoEmitido.current) {
      setDisplay(isoAFechaDisplay(value));
      ultimoEmitido.current = value;
    }
  }, [value]);

  const iso = displayFechaAIso(display);
  const vacio = display.trim() === '';
  const completo = display.length === 10;

  let error: string | null = null;
  if (vacio) {
    if (requerido) error = 'La fecha es obligatoria.';
  } else if (!completo) {
    error = 'Fecha incompleta (dd-mm-aaaa).';
  } else if (iso === '') {
    error = 'Fecha inválida.';
  } else if (min && iso < min) {
    error = `No puede ser anterior a ${isoAFechaDisplay(min)}.`;
  } else if (max && iso > max) {
    error = `No puede ser posterior a ${isoAFechaDisplay(max)}.`;
  }

  const mostrarError = tocado && error !== null;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const fmt = formatearFechaEnVivo(e.target.value);
    setDisplay(fmt);
    const nuevoIso = displayFechaAIso(fmt);
    const dentroRango =
      nuevoIso !== '' &&
      (!min || nuevoIso >= min) &&
      (!max || nuevoIso <= max);
    const emit = dentroRango ? nuevoIso : '';
    ultimoEmitido.current = emit;
    onChange(emit);
  }

  return (
    <>
      <input
        type="text"
        inputMode="numeric"
        id={id}
        value={display}
        onChange={handleChange}
        onBlur={() => setTocado(true)}
        maxLength={10}
        disabled={disabled}
        placeholder="dd-mm-aaaa"
        aria-label={ariaLabel}
        aria-invalid={mostrarError || undefined}
        className={`${CLASE_BASE} ${className}`.trim()}
      />
      {mostrarError && (
        <p className="text-xs text-state-danger mt-1" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
